import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { Toaster } from "sonner";
import LoginPage from "@/pages/LoginPage";
import SignupPage from "@/pages/SignupPage";
import StudentDashboard from "@/pages/StudentDashboard";
import InstructorDashboard from "@/pages/InstructorDashboard";
import GuardianDashboard from "@/pages/GuardianDashboard";
import ModuleView from "@/pages/ModuleView";
import AdminDashboard from "@/pages/AdminDashboard";
import AdminCourses from "@/pages/AdminCourses";
import AdminCourseDashboard from "@/pages/AdminCourseDashboard";
import AdminGroups from "@/pages/AdminGroups";
import AdminModules from "@/pages/AdminModules";
import AdminModuleEdit from "@/pages/AdminModuleEdit";
import AdminStudents from "@/pages/AdminStudents";
import AdminUsers from "@/pages/AdminUsers";
import AdminRoles from "@/pages/AdminRoles";
import AdminInventory from "@/pages/AdminInventory";
import AdminSessions from "@/pages/AdminSessions";
import AdminSessionEdit from "@/pages/AdminSessionEdit";
import AdminPayments from "@/pages/AdminPayments";
import AdminInvites from "@/pages/AdminInvites";
import AdminEnroll from "@/pages/AdminEnroll";
import StudioFeed from "@/pages/StudioFeed";
import PublicPostView from "@/pages/PublicPostView";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AdminLayout from "@/components/AdminLayout";
import StudentLayout from "@/components/StudentLayout";

function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F3EF] flex items-center justify-center">
        <div className="text-[#5C3A2E] font-heading text-2xl">Loading...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin" && user.role !== "custom" && user.role !== "instructor") return <Navigate to="/" replace />;
  return children;
}

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F7F3EF] flex items-center justify-center">
        <div className="text-[#5C3A2E] font-heading text-2xl">Loading...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === "admin") return <Navigate to="/admin" replace />;
  if (user.role === "custom") return <Navigate to="/admin" replace />;
  if (user.role === "instructor") return <Navigate to="/admin" replace />;
  if (user.role === "guardian") return <GuardianDashboard />;
  if (user.role === "spectator") return <Navigate to="/feed" replace />;
  return <StudentDashboard />;
}

function FeedRouter() {
  const { user } = useAuth();
  if (user?.role === "admin") return <StudioFeed layout={AdminLayout} />;
  return <StudioFeed layout={StudentLayout} />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/" element={<HomeRedirect />} />
          <Route path="/module/:id" element={<ProtectedRoute><ModuleView /></ProtectedRoute>} />
          <Route path="/admin" element={<ProtectedRoute adminOnly><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/courses" element={<ProtectedRoute adminOnly><AdminCourses /></ProtectedRoute>} />
          <Route path="/admin/courses/:courseId" element={<ProtectedRoute adminOnly><AdminCourseDashboard /></ProtectedRoute>} />
          <Route path="/admin/groups" element={<ProtectedRoute adminOnly><AdminGroups /></ProtectedRoute>} />
          <Route path="/admin/modules" element={<ProtectedRoute adminOnly><AdminModules /></ProtectedRoute>} />
          <Route path="/admin/modules/:id" element={<ProtectedRoute adminOnly><AdminModuleEdit /></ProtectedRoute>} />
          <Route path="/admin/students" element={<ProtectedRoute adminOnly><AdminStudents /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute adminOnly><AdminUsers /></ProtectedRoute>} />
          <Route path="/admin/roles" element={<ProtectedRoute adminOnly><AdminRoles /></ProtectedRoute>} />
          <Route path="/admin/inventory" element={<ProtectedRoute adminOnly><AdminInventory /></ProtectedRoute>} />
          <Route path="/admin/sessions" element={<ProtectedRoute adminOnly><AdminSessions /></ProtectedRoute>} />
          <Route path="/admin/sessions/:id" element={<ProtectedRoute adminOnly><AdminSessionEdit /></ProtectedRoute>} />
          <Route path="/admin/payments" element={<ProtectedRoute adminOnly><AdminPayments /></ProtectedRoute>} />
          <Route path="/admin/invites" element={<ProtectedRoute adminOnly><AdminInvites /></ProtectedRoute>} />
          <Route path="/admin/enroll" element={<ProtectedRoute adminOnly><AdminEnroll /></ProtectedRoute>} />
          <Route path="/admin/feed" element={<ProtectedRoute adminOnly><StudioFeed layout={AdminLayout} /></ProtectedRoute>} />
          <Route path="/feed" element={<ProtectedRoute><FeedRouter /></ProtectedRoute>} />
          <Route path="/post/:id" element={<PublicPostView />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
